import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';

import BomUnitsAnalysisInProgress
  from '../../../AICreatorBoMUnits/components/BomUnitsAnalysisInProgress/BomUnitsAnalysisInProgress';
import RowAICreatorSupplier from '../RowAICreatorSupplier/RowAICreatorSupplier';

import type ISupplierUnitsTable from './interfaces/ISupplierUnitsTable';

import styles from './scss/SupplierUnitsTable.module.scss';

function SupplierUnitsTable({
  open,
  isAnalysisInProgress,
  isSelectAllLoading,
  isSelectAllChecked,
  onSelectAllChange,
  tableItems,
  naceOptions,
  countryOptions,
  onUpdate,
  onDelete,
  onLoadMore,
  hasMore,
  emptyStateMessage,
  focusCell,
  onStartEdit,
}: ISupplierUnitsTable) {
  const { t } = useTranslation();
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [tableScrollbarWidth, setTableScrollbarWidth] = useState(0);
  const [tableWrapperWidth, setTableWrapperWidth] = useState(0);

  const tableHeaders = useMemo(() => ([
    {
      id: 'supplierName',
      title: t('valueChainMap.supplierUnitsSupplierName'),
      width: 160,
    },
    {
      id: 'code',
      title: t('valueChainMap.supplierUnitsCode'),
      width: 140,
    },
    {
      id: 'location',
      title: t('valueChainMap.supplierUnitsLocation'),
      width: 170,
    },
    {
      id: 'hq',
      title: t('valueChainMap.supplierUnitsHq'),
      width: 190,
    },
    {
      id: 'goodsType',
      title: t('valueChainMap.supplierUnitsGoodsType'),
      width: 220,
    },
    {
      id: 'website',
      title: t('valueChainMap.supplierUnitsWebsite'),
      width: 150,
    },
    {
      id: 'naceSector',
      title: t('valueChainMap.supplierUnitsNaceSector'),
      width: 250,
    },
    {
      id: 'source',
      title: t('valueChainMap.supplierUnitsSource'),
      width: 100,
    },
  ]), [t]);

  const visibleColumnsCount = 1 + tableHeaders.length + 1;
  const tableBaseWidth = useMemo(
    () => tableHeaders.reduce((sum, header) => sum + header.width, 48 + 64),
    [tableHeaders],
  );
  const tableAvailableWidth = Math.max(tableWrapperWidth - tableScrollbarWidth, 0);
  const tableMinScale = 0.88;
  const tableScale = useMemo(() => {
    if (!tableAvailableWidth) {
      return 1;
    }

    return Math.max(Math.min(tableAvailableWidth / tableBaseWidth, 1), tableMinScale);
  }, [tableAvailableWidth, tableBaseWidth]);
  const tableWidth = Math.round(tableBaseWidth * tableScale);

  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;

    if (!tableWrapper) {
      return undefined;
    }

    const syncTableWrapperWidth = () => {
      setTableWrapperWidth(tableWrapper.clientWidth);
    };

    syncTableWrapperWidth();

    const resizeObserver = new ResizeObserver(() => {
      syncTableWrapperWidth();
    });

    resizeObserver.observe(tableWrapper);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const syncTableScrollbarWidth = () => {
      const tableScrollContainer = tableScrollContainerRef.current;

      if (!tableScrollContainer) {
        setTableScrollbarWidth(0);
        return;
      }

      const nextScrollbarWidth = tableScrollContainer.offsetWidth - tableScrollContainer.clientWidth;
      setTableScrollbarWidth(nextScrollbarWidth);
    };

    syncTableScrollbarWidth();
    window.addEventListener('resize', syncTableScrollbarWidth);

    return () => {
      window.removeEventListener('resize', syncTableScrollbarWidth);
    };
  }, [open, tableItems.length, isAnalysisInProgress]);

  const renderTableColGroup = () => (
    <colgroup>
      <col style={{ width: `${Math.round(48 * tableScale)}px` }} />
      {tableHeaders.map((column) => (
        <col key={column.id} style={{ width: `${Math.round(column.width * tableScale)}px` }} />
      ))}
      <col style={{ width: `${Math.round(64 * tableScale)}px` }} />
    </colgroup>
  );

  return (
    <div
      ref={tableWrapperRef}
      className={styles.tableWrapper}
      style={{ '--supplier-units-table-width': `${tableWidth}px` } as React.CSSProperties}
    >
      <div className={styles.tableHeaderContainer} style={{ paddingRight: `${tableScrollbarWidth}px` }}>
        <Table className={styles.table}>
          {renderTableColGroup()}
          <TableHead className={styles.tableHead}>
            <TableCell head className={clsx(styles.tableCellHead, styles.checkboxHeadCell)}>
              <Checkbox
                name="supplierUnitsSelectAll"
                value="all"
                checked={isSelectAllChecked}
                onChange={onSelectAllChange}
                disabled={isAnalysisInProgress || isSelectAllLoading || tableItems.length === 0}
                className={styles.selectAllCheckbox}
              />
            </TableCell>
            {tableHeaders.map((header) => (
              <TableCell
                key={header.id}
                head
                className={clsx(
                  styles.tableCellHead,
                  header.id === 'supplierName' && styles.nameHeadCell,
                  header.id === 'code' && styles.codeHeadCell,
                  header.id === 'location' && styles.locationHeadCell,
                  header.id === 'hq' && styles.hqHeadCell,
                  header.id === 'goodsType' && styles.goodsTypeHeadCell,
                  header.id === 'website' && styles.websiteHeadCell,
                  header.id === 'naceSector' && styles.naceHeadCell,
                  header.id === 'source' && styles.sourceHeadCell,
                )}
              >
                {header.title}
              </TableCell>
            ))}
            <TableCell head className={clsx(styles.tableCellHead, styles.actionHeadCell)}>
              <span aria-hidden="true" />
            </TableCell>
          </TableHead>
        </Table>
      </div>

      <div
        id="supplier-units-table-scroll"
        ref={tableScrollContainerRef}
        className={styles.tableScrollContainer}
      >
        {isAnalysisInProgress ? (
          <Table className={styles.table}>
            {renderTableColGroup()}
            <TableBody className={styles.tableBody}>
              <tr>
                <TableCell colSpan={visibleColumnsCount} className={styles.emptyStateCell}>
                  <BomUnitsAnalysisInProgress />
                </TableCell>
              </tr>
            </TableBody>
          </Table>
        ) : (
          <InfiniteScroll
            className={styles.tableScroll}
            dataLength={tableItems.length}
            next={onLoadMore}
            hasMore={hasMore}
            loader={false}
            scrollableTarget="supplier-units-table-scroll"
            style={{ overflow: 'visible' }}
          >
            <Table className={styles.table}>
              {renderTableColGroup()}
              <TableBody className={styles.tableBody}>
                {tableItems.length ? tableItems.map((item) => (
                  <RowAICreatorSupplier
                    key={item.id}
                    item={item}
                    naceOptions={naceOptions}
                    countryOptions={countryOptions}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    focusCell={focusCell}
                    onStartEdit={onStartEdit}
                  />
                )) : (
                  <tr>
                    <TableCell colSpan={visibleColumnsCount} className={styles.emptyStateCell}>
                      <div className={styles.emptyState}>
                        <InfoIcon className={styles.emptyStateIcon} />
                        <p className={styles.emptyStateText}>
                          {emptyStateMessage}
                        </p>
                      </div>
                    </TableCell>
                  </tr>
                )}
              </TableBody>
            </Table>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}

export default SupplierUnitsTable;
